/** /mcp-diagnose-runs — the client MCP diagnose's runs and their review queue (client MCP plan
 *  §5.5, §7.3 #4; WPS FE-07, backend P4-03).
 *
 *  A client holding `triage.mcp_diagnose` asks its AI why something happened; our diagnose
 *  answers in prose, and an LLM judge reads it before release — fail-closed: anything but
 *  "Released" withholds it and the client gets the checks' findings. Every withheld answer and a
 *  sample of the released ones are flagged for a person: `Review = Waiting for review` is the
 *  queue (the menu opens it).
 *
 *  One row per run with our facts only — never the client's question or an answer (those are on
 *  the run's page, marked untrusted). A row opens the run, where it is reviewed.
 *
 *  Filters live in the URL (linkable, preset-covered): tenant (console scope aware), review,
 *  the flag's reason, the judge's verdict, the review's outcome and the asked window. A value the
 *  endpoint does not know is dropped (it would 400 the grid). Gated by `console_entity_ai`. */
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Link,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { formatUsd } from '@/api/aiSpend';
import * as runsApi from '@/api/mcpDiagnoseRuns';
import {
  answerText,
  JUDGE_OUTCOMES,
  judgeColor,
  judgeLabel,
  mcpDiagnoseRunKeys,
  REVIEW_FILTERS,
  REVIEW_OUTCOMES,
  REVIEW_REASONS,
  reviewPhrase,
  reviewReasonColor,
  reviewReasonLabel,
  runStateColor,
  runStateLabel,
  SUBJECT_SCOPE_LABELS,
  type McpDiagnoseRun,
} from '@/api/mcpDiagnoseRuns';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { boundToParam, paramToBound, TimeWindowPicker } from '@/components/TimeWindowPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatDate, formatRelativeTime } from '@/utils/format';

import {
  askerActions,
  judgeActions,
  mcpDiagnoseRunHref,
  reasonActions,
  runActions,
  tenantGrid,
} from './mcpDiagnoseRunLinks';
import { SubjectCell } from './SubjectCell';

/** A run as a grid row: its task id is its row id. */
type RunRow = McpDiagnoseRun & { id: string };

/** An ISO instant or a relative token (`now-7d`) — the backend reads both. */
const isoOrDrop = (v: string) => (paramToBound(v) ? v : undefined);

/** A comma list kept to the known values — an unknown one would 400 the whole grid. */
const knownList =
  (known: readonly { value: string }[]) =>
  (v: string): string | undefined => {
    const values = v.split(',').filter((x) => known.some((k) => k.value === x));
    return values.length ? values.join(',') : undefined;
  };

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'review', toApi: (v) => (REVIEW_FILTERS.some((f) => f.value === v) ? v : undefined) },
  { param: 'reason', toApi: knownList(REVIEW_REASONS) },
  { param: 'judge', toApi: knownList(JUDGE_OUTCOMES) },
  { param: 'outcome', toApi: knownList(REVIEW_OUTCOMES) },
  { param: 'from', apiParam: 'created_after', toApi: isoOrDrop },
  { param: 'to', apiParam: 'created_before', toApi: isoOrDrop },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'task_id',
  'created_ts',
  'tenant',
  'user_email',
  'subject',
  'state',
  'status',
  'judge',
  'review',
  'cost_usd',
  'finished_ts',
  'id',
];
const DEFAULT_HIDDEN = ['finished_ts', 'id'];

/** A grid cell link that navigates by itself (cmd-click opens a tab) instead of the row. */
function CellLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link component={RouterLink} to={to} underline="hover" onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

function When({ iso }: { iso: string | null }) {
  if (!iso) return <>—</>;
  return (
    <Tooltip title={formatDate(iso)} describeChild>
      <span>{formatRelativeTime(iso)}</span>
    </Tooltip>
  );
}

function MultiSelect<V extends string>({
  label,
  options,
  values,
  width,
  onChange,
}: {
  label: string;
  options: readonly { value: V; label: string }[];
  values: string[];
  width: number;
  onChange: (next: string | null) => void;
}) {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  return (
    <TextField
      select
      size="small"
      label={label}
      value={values}
      onChange={(e) => onChange((e.target.value as unknown as string[]).join(',') || null)}
      sx={{ width }}
      SelectProps={{
        multiple: true,
        renderValue: (sel) => (sel as string[]).map(labelOf).join(', '),
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          <Checkbox size="small" checked={values.includes(o.value)} sx={{ py: 0 }} />
          <ListItemText primary={o.label} />
        </MenuItem>
      ))}
    </TextField>
  );
}

export function McpDiagnoseRunsPage() {
  const navigate = useNavigate();

  const presets = useGridPresets({
    grid: 'mcp_diagnose_runs',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const listOf = (param: string) => (getParam(param) ?? '').split(',').filter(Boolean);
  const review = getParam('review') ?? '';
  const reasons = listOf('reason');
  const judges = listOf('judge');
  const outcomes = listOf('outcome');
  const from = getParam('from');
  const to = getParam('to');

  const columns = useMemo<GridColDef<RunRow>[]>(
    () => [
      {
        field: 'task_id',
        headerName: 'Run',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <ValueActions actions={runActions(p.row.task_id)}>
            <CellLink to={mcpDiagnoseRunHref(p.row.task_id)}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                {p.row.task_id.slice(0, 8)}
              </Box>
            </CellLink>
          </ValueActions>
        ),
      },
      {
        field: 'created_ts',
        headerName: 'Asked',
        width: 130,
        filterable: false,
        renderCell: (p) => <When iso={p.row.created_ts} />,
      },
      {
        field: 'tenant',
        headerName: 'Tenant',
        width: 140,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.tenant?.code ?? `#${row.tour_operator_id}`,
        renderCell: (p) =>
          p.row.tenant ? (
            <ValueActions actions={operatorActions(p.row.tenant, tenantGrid(p.row.tenant.id))}>
              {p.row.tenant.code || p.row.tenant.name}
            </ValueActions>
          ) : (
            `#${p.row.tour_operator_id}`
          ),
      },
      {
        field: 'user_email',
        headerName: 'Asked by',
        width: 210,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.user_email ? (
            <ValueActions actions={askerActions({ id: p.row.user_id, email: p.row.user_email })}>
              {p.row.user_email}
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'subject',
        headerName: 'Subject',
        width: 150,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) =>
          `${SUBJECT_SCOPE_LABELS[row.subject.scope] ?? row.subject.scope} ${row.subject.booking_id ?? ''}`.trim(),
        renderCell: (p) => <SubjectCell subject={p.row.subject} />,
      },
      {
        field: 'state',
        headerName: 'Run',
        width: 120,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => runStateLabel(row.state),
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            color={runStateColor(p.row.state)}
            label={runStateLabel(p.row.state)}
          />
        ),
      },
      {
        field: 'status',
        headerName: 'The client’s answer',
        width: 250,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => answerText(row),
      },
      {
        field: 'judge',
        headerName: 'Judge',
        width: 160,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => (row.judge ? judgeLabel(row.judge.outcome) : 'Not judged'),
        renderCell: (p) =>
          p.row.judge ? (
            <ValueActions chip actions={judgeActions(p.row.judge.outcome)}>
              <Tooltip
                describeChild
                title={p.row.judge.categories.length ? p.row.judge.categories.join(', ') : ''}
              >
                <Chip
                  size="small"
                  color={judgeColor(p.row.judge.outcome)}
                  label={judgeLabel(p.row.judge.outcome)}
                />
              </Tooltip>
            </ValueActions>
          ) : (
            <Typography variant="body2" component="span" color="text.secondary">
              Not judged
            </Typography>
          ),
      },
      {
        field: 'review',
        headerName: 'Review',
        width: 260,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => reviewPhrase(row.review),
        renderCell: (p) =>
          p.row.review ? (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
              <Chip
                size="small"
                color={
                  p.row.review.state === 'pending'
                    ? 'warning'
                    : p.row.review.outcome === 'problem'
                      ? 'error'
                      : 'success'
                }
                label={reviewPhrase(p.row.review)}
              />
              <ValueActions chip actions={reasonActions(p.row.review.reason)}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={reviewReasonColor(p.row.review.reason)}
                  label={reviewReasonLabel(p.row.review.reason)}
                />
              </ValueActions>
            </Stack>
          ) : (
            <Typography variant="body2" component="span" color="text.secondary">
              Not flagged
            </Typography>
          ),
      },
      {
        field: 'cost_usd',
        headerName: 'Cost',
        width: 90,
        type: 'number',
        sortable: false,
        filterable: false,
        valueFormatter: (value: number | null) => (value === null ? '—' : formatUsd(value)),
      },
      {
        field: 'finished_ts',
        headerName: 'Finished',
        width: 130,
        filterable: false,
        renderCell: (p) => <When iso={p.row.finished_ts} />,
      },
      copyableColumn({
        field: 'id',
        headerName: 'ID',
        width: 300,
        sortable: false,
        filterable: false,
      }),
    ],
    [],
  );

  const queryFn = async (params: ResourceDataGridParams) => {
    const page = await runsApi.list({
      ...(params.extra as runsApi.McpDiagnoseRunFilters),
      ordering: params.ordering ?? presets.ordering,
      ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
      page: params.page,
      page_size: params.pageSize,
    });
    return { ...page, results: page.results.map((r): RunRow => ({ ...r, id: r.task_id })) };
  };

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'review',
      priority: 7,
      active: review !== '',
      node: (
        <TextField
          select
          size="small"
          label="Review"
          value={review}
          onChange={(e) => setParam('review', e.target.value || null)}
          sx={{ width: 190 }}
        >
          <MenuItem value="">Any</MenuItem>
          {REVIEW_FILTERS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'when',
      priority: 6,
      active: Boolean(from || to),
      node: (
        <TimeWindowPicker
          label="Asked"
          allowOpenEnded
          value={[paramToBound(from), paramToBound(to, { end: true })]}
          onChange={([start, end]) =>
            setParams({ from: boundToParam(start), to: boundToParam(end) })
          }
        />
      ),
    },
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'judge',
      priority: 4,
      active: judges.length > 0,
      node: (
        <MultiSelect
          label="Judge"
          options={JUDGE_OUTCOMES}
          values={judges}
          width={180}
          onChange={(v) => setParam('judge', v)}
        />
      ),
    },
    {
      key: 'reason',
      priority: 3,
      active: reasons.length > 0,
      node: (
        <MultiSelect
          label="Flagged because"
          options={REVIEW_REASONS}
          values={reasons}
          width={190}
          onChange={(v) => setParam('reason', v)}
        />
      ),
    },
    {
      key: 'outcome',
      priority: 2,
      active: outcomes.length > 0,
      node: (
        <MultiSelect
          label="Review outcome"
          options={REVIEW_OUTCOMES}
          values={outcomes}
          width={170}
          onChange={(v) => setParam('outcome', v)}
        />
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="mcp-diagnose-runs"
        title="Diagnose reviews"
        subtitle="Clients’ MCP diagnose runs, the judge’s verdict on each AI-written answer, and the human review of the withheld and sampled ones."
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<RunRow>
          queryKey={mcpDiagnoseRunKeys.list}
          queryFn={queryFn}
          allowExtendedTimeout
          resetPageOnQueryChange
          columns={orderColumns(columns, presets.orderedFields)}
          defaultSort={{ field: 'created_ts', sort: 'desc' }}
          extra={extra}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          onRowClick={(_id, row) => navigate(mcpDiagnoseRunHref(row.task_id))}
          emptyMessage="No client diagnose runs match. They appear here when a client’s AI asks our diagnose a question over MCP."
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            exportFileName: 'mcp-diagnose-runs',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
